import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Zap, Plus, Loader2, CheckCircle2, XCircle, Eye, Play, RotateCcw, ShieldCheck,
} from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState, StatusPill } from "@/components/common";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { shortId } from "@/lib/utils";

interface ActionDef {
  id: string; name: string; connector_key: string;
  requires_approval: boolean; enabled: boolean; config: Record<string, unknown>;
  needs_review?: boolean;
}
interface Pipeline { id: string; name: string }
interface PipelineRun { id: string; pipeline: string; pipeline_revision?: string | null; ontology_version?: string | null; status: string }
interface Integration { id: string; name: string; kind: string; purpose: string; active: boolean; secret_ref: string; revision: number; digest: string }
interface ActionRun {
  id: string; action: string; status: string; dry_run: boolean;
  external_id?: string; error?: string; idempotency_key?: string;
  decision_kind?: string; decision_ref?: string;
  result?: Record<string, unknown>;
}
interface CompensationRequest {
  id: string; run: string; status: string; reason: string;
  requested_by: string; approved_by?: string; attempts: number;
  reconciliation?: Record<string, unknown>; error?: string;
}

export function Actions() {
  const ws = useApp((s) => s.workspaceId);
  const { canEdit, canAdmin } = useWorkspacePermissions();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [compensateRun, setCompensateRun] = useState<string | null>(null);
  const [compensationReason, setCompensationReason] = useState("");
  const [selectedPipelineRun, setSelectedPipelineRun] = useState<Record<string, string>>({});
  const [rejectRunId, setRejectRunId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const defs = useQuery<{ results: ActionDef[] }>({
    queryKey: ["action-defs", ws],
    queryFn: () => rawFetch(`/actions/definitions/?page_size=100${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const runs = useQuery<{ results: ActionRun[] }>({
    queryKey: ["action-runs", ws],
    queryFn: () => rawFetch(`/actions/runs/?page_size=50&ordering=-created_at${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const compensations = useQuery<{ results: CompensationRequest[] }>({
    queryKey: ["compensation-requests", ws],
    queryFn: () => rawFetch(`/actions/compensation-requests/?page_size=100&ordering=-created_at${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const pipelines = useQuery<{ results: Pipeline[] }>({
    queryKey: ["action-pipelines", ws],
    queryFn: () => rawFetch(`/pipelines/pipelines/?workspace=${ws}&page_size=100`), enabled: !!ws,
  });
  const pipelineRuns = useQuery<{ results: PipelineRun[] }>({
    queryKey: ["action-pipeline-runs", ws],
    queryFn: () => rawFetch(`/pipelines/runs/?workspace=${ws}&status=succeeded&page_size=100`), enabled: !!ws,
  });
  const integrations = useQuery<Integration[]>({
    queryKey: ["action-integrations", ws],
    queryFn: () => rawFetch(`/pipelines/integrations/?workspace=${ws}`), enabled: !!ws,
  });

  const runMut = (verb: string) => useMutation({
    mutationFn: (id: string) => rawFetch(`/actions/runs/${id}/${verb}/`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action-runs", ws] }),
  });
  const approve = runMut("approve");
  const reject = useMutation({
    mutationFn: (id: string) => rawFetch(`/actions/runs/${id}/reject/`, {
      method: "POST", body: JSON.stringify({ reason: rejectReason.trim() }),
    }),
    onSuccess: () => { setRejectRunId(null); setRejectReason(""); qc.invalidateQueries({ queryKey: ["action-runs", ws] }); },
  });
  const dryRun = runMut("dry_run");
  const execute = runMut("execute");

  const requestCompensation = useMutation({
    mutationFn: () => rawFetch(`/actions/compensation-requests/request/`, {
      method: "POST",
      body: JSON.stringify({ run_id: compensateRun, reason: compensationReason.trim(), workspace: ws }),
    }),
    onSuccess: () => {
      setCompensateRun(null);
      setCompensationReason("");
      qc.invalidateQueries({ queryKey: ["compensation-requests", ws] });
    },
  });
  const compensationMut = (verb: string) => useMutation({
    mutationFn: (id: string) => rawFetch(`/actions/compensation-requests/${id}/${verb}/`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["compensation-requests", ws] });
      qc.invalidateQueries({ queryKey: ["action-runs", ws] });
    },
  });
  const approveCompensation = compensationMut("approve");
  const rejectCompensation = compensationMut("reject");
  const executeCompensation = compensationMut("execute");
  const retryCompensation = compensationMut("retry");

  const request = useMutation({
    mutationFn: ({ defId, runId }: { defId: string; runId?: string }) => rawFetch(`/actions/definitions/${defId}/${runId ? "request-from-pipeline" : "request"}/`, {
      method: "POST", body: JSON.stringify(runId ? { pipeline_run_id: runId } : { payload: {} }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action-runs", ws] }),
  });
  const toggle = useMutation({
    mutationFn: (def: ActionDef) => rawFetch(`/actions/definitions/${def.id}/`, {
      method: "PATCH", body: JSON.stringify({ enabled: !def.enabled }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action-defs", ws] }),
  });

  if (!ws) {
    return (
      <>
        <PageHeader title="Actions" subtitle="Closed-loop, governed write-back to external systems." />
        <EmptyState title="Select a workspace first." />
      </>
    );
  }

  const busy = (m: typeof approve, id: string) => m.isPending && m.variables === id;
  const readyRuns = (def: ActionDef) => (pipelineRuns.data?.results ?? []).filter((row) =>
    row.pipeline_revision === def.config.pipeline_revision_id && row.ontology_version === def.config.ontology_version_id);

  return (
    <>
      <PageHeader
        title="Actions"
        subtitle="Review a completed pipeline decision before it writes to a downstream system."
        help="Pin a saved pipeline and published ontology. Preview the receipt, approve it, then execute. External actions need a reviewed integration. Compensation is available only where the connector supports it."
        learnMore={{ to: "/howto", label: "Platform how-to" }}
        actions={
          <button className="btn-primary" disabled={!canEdit} onClick={() => setShowCreate((v) => !v)}>
            <Plus size={15} className="mr-1 inline" /> New action
          </button>
        }
      />

      {showCreate && <CreateAction ws={ws} pipelines={pipelines.data?.results ?? []} runs={pipelineRuns.data?.results ?? []}
        integrations={integrations.data ?? []} onDone={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ["action-defs", ws] }); }} />}
      {compensateRun && (
        <div className="card mb-4 border-warn/40">
          <div className="mb-1 text-sm font-medium">Request compensation</div>
          <div className="mb-3 text-xs text-muted">
            Run <code>{shortId(compensateRun)}</code> stays unchanged until this request is independently approved and executed.
          </div>
          <textarea
            className="input min-h-[72px]"
            placeholder="Required rationale for reversing the external action"
            value={compensationReason}
            onChange={(event) => setCompensationReason(event.target.value)}
          />
          <div className="mt-3 flex gap-2">
            <button className="btn-primary" disabled={!compensationReason.trim() || requestCompensation.isPending} onClick={() => requestCompensation.mutate()}>
              {requestCompensation.isPending ? <Loader2 className="mr-1 inline animate-spin" size={14} /> : <RotateCcw className="mr-1 inline" size={14} />}
              Submit request
            </button>
            <button className="btn-ghost" onClick={() => { setCompensateRun(null); setCompensationReason(""); }}>Cancel</button>
          </div>
          {requestCompensation.isError && <div className="mt-2"><ErrorBox error={requestCompensation.error} /></div>}
        </div>
      )}

      {/* Definitions */}
      <h2 className="mb-2 mt-2 text-sm font-semibold">Action definitions</h2>
      {defs.isError && <ErrorBox error={defs.error} />}
      {(defs.data?.results?.length ?? 0) === 0 ? (
        <EmptyState title="No actions yet." hint="Complete a pipeline run, then pin an action to that run's pipeline and ontology." />
      ) : (
        <div className="mb-6 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          {defs.data!.results.map((d) => (
            <div key={d.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Zap size={16} className="text-accent" />
                  <div>
                    <div className="text-sm font-medium">{d.name}</div>
                    <span className="text-[11px] text-muted">{d.connector_key === "action.log" ? "Internal decision record" : "Reviewed HTTP action"}</span>
                  </div>
                </div>
                {d.requires_approval && (
                  <span className="pill-warn flex items-center gap-1 text-[10px]">
                    <ShieldCheck size={11} /> approval
                  </span>
                )}
              </div>
              <div className="mt-2 text-xs text-muted">
                {d.config.pipeline_revision_id ? "Pinned to a saved pipeline and published ontology" : "Manual log receipt"}
                {d.needs_review ? " · Integration needs review" : ""}
                {!d.enabled ? " · Disabled" : ""}
              </div>
              {d.config.pipeline_revision_id ? (
                <div className="mt-3 flex gap-2">
                  <select aria-label={`Completed run for ${d.name}`} className="input min-w-0 flex-1"
                    value={selectedPipelineRun[d.id] ?? ""}
                    onChange={(e) => setSelectedPipelineRun((current) => ({ ...current, [d.id]: e.target.value }))}>
                    <option value="">Choose completed run</option>
                    {readyRuns(d).map((row) => <option key={row.id} value={row.id}>
                      {pipelines.data?.results.find((p) => p.id === row.pipeline)?.name ?? "Pipeline"} · {shortId(row.id)}
                    </option>)}
                  </select>
                  <button className="btn-secondary" disabled={!canEdit || !d.enabled || !selectedPipelineRun[d.id] || request.isPending}
                    onClick={() => request.mutate({ defId: d.id, runId: selectedPipelineRun[d.id] })}>Request</button>
                </div>
              ) : <button className="btn-ghost mt-3 w-full text-xs" disabled={!canEdit || !d.enabled || d.connector_key !== "action.log" || request.isPending}
                onClick={() => request.mutate({ defId: d.id })}>Request manual receipt</button>}
              {canEdit && <button className="btn-ghost mt-2 text-xs" disabled={toggle.isPending} onClick={() => toggle.mutate(d)}>
                {d.enabled ? "Disable action" : "Enable action"}
              </button>}
            </div>
          ))}
        </div>
      )}
      {request.isError && <ErrorBox error={request.error} />}
      {toggle.isError && <ErrorBox error={toggle.error} />}

      {/* Runs queue */}
      <h2 className="mb-2 text-sm font-semibold">Run queue</h2>
      {runs.isError && <ErrorBox error={runs.error} />}
      {(runs.data?.results?.length ?? 0) === 0 ? (
        <EmptyState title="No runs yet." />
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="min-w-full text-sm">
            <thead className="bg-border/30 text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Run</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Outcome</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.data!.results.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2"><div className="text-xs font-medium">{defs.data?.results.find((d) => d.id === r.action)?.name ?? "Action"}</div>
                    <code className="text-[11px] text-muted">{shortId(r.id)}</code>
                    {r.dry_run && <div className="text-[11px] text-good">Preview complete</div>}</td>
                  <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-3 py-2 text-xs text-muted">{r.external_id?.startsWith("log:") ? "Decision recorded" : r.external_id || "Pending"}</td>
                  <td className="px-3 py-2 text-xs text-muted">
                    {r.decision_kind === "pipeline_run" ?
                      pipelines.data?.results.find((p) => p.id === pipelineRuns.data?.results.find((run) => run.id === r.decision_ref)?.pipeline)?.name ?? "Completed pipeline" :
                      r.decision_kind ? "Platform event" : "Manual"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {r.status === "pending_approval" && (
                        <>
                          {canAdmin && <IconBtn label="Approve" tone="good" busy={busy(approve, r.id)} onClick={() => approve.mutate(r.id)}><CheckCircle2 size={14} /></IconBtn>}
                          {canAdmin && <IconBtn label="Reject" tone="bad" busy={busy(reject, r.id)} onClick={() => setRejectRunId(r.id)}><XCircle size={14} /></IconBtn>}
                          {canAdmin && <IconBtn label="Preview" busy={busy(dryRun, r.id)} onClick={() => dryRun.mutate(r.id)}><Eye size={14} /></IconBtn>}
                        </>
                      )}
                      {r.status === "approved" && (
                        <>
                          {canAdmin && <IconBtn label="Preview" busy={busy(dryRun, r.id)} onClick={() => dryRun.mutate(r.id)}><Eye size={14} /></IconBtn>}
                          {canAdmin && <IconBtn label="Execute" tone="good" busy={busy(execute, r.id)} onClick={() => execute.mutate(r.id)}><Play size={14} /></IconBtn>}
                        </>
                      )}
                      {r.status === "succeeded" && canEdit && (
                        <IconBtn label="Request compensation" onClick={() => setCompensateRun(r.id)}><RotateCcw size={14} /></IconBtn>
                      )}
                      {!["pending_approval", "approved", "succeeded"].includes(r.status) && (
                        <span className="text-xs text-muted">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {[approve, reject, dryRun, execute].map((m, i) =>
        m.isError ? <div key={i} className="mt-2"><ErrorBox error={m.error} /></div> : null)}
      {rejectRunId && <div className="card mt-3"><label className="text-sm">Reason for rejection
        <textarea className="input mt-1 min-h-20" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
      </label><div className="mt-2 flex gap-2"><button className="btn-primary" disabled={!rejectReason.trim() || reject.isPending} onClick={() => reject.mutate(rejectRunId)}>Reject request</button>
        <button className="btn-ghost" onClick={() => setRejectRunId(null)}>Cancel</button></div></div>}

      <h2 className="mb-2 mt-6 text-sm font-semibold">Compensation and reconciliation</h2>
      {compensations.isError && <ErrorBox error={compensations.error} />}
      {(compensations.data?.results?.length ?? 0) === 0 ? (
        <EmptyState title="No compensation requests." hint="Request one from a succeeded action run when an external write must be reversed." />
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="min-w-full text-sm">
            <thead className="bg-border/30 text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Request</th>
                <th className="px-3 py-2 font-medium">Run</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Reason</th>
                <th className="px-3 py-2 font-medium">Evidence</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {compensations.data!.results.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2"><code className="text-xs">{shortId(item.id)}</code></td>
                  <td className="px-3 py-2"><code className="text-xs">{shortId(item.run)}</code></td>
                  <td className="px-3 py-2"><StatusPill status={item.status} /></td>
                  <td className="max-w-[280px] px-3 py-2 text-xs">{item.reason}</td>
                  <td className="px-3 py-2 text-xs text-muted">
                    {item.status === "reconciled" ? `Connector acknowledged · attempt ${item.attempts}` : item.error || `Attempt ${item.attempts}`}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      {item.status === "proposed" && (
                        <>
                          <IconBtn label="Approve compensation" tone="good" busy={busy(approveCompensation, item.id)} onClick={() => approveCompensation.mutate(item.id)}><CheckCircle2 size={14} /></IconBtn>
                          <IconBtn label="Reject compensation" tone="bad" busy={busy(rejectCompensation, item.id)} onClick={() => rejectCompensation.mutate(item.id)}><XCircle size={14} /></IconBtn>
                        </>
                      )}
                      {item.status === "approved" && (
                        <IconBtn label="Execute compensation" tone="good" busy={busy(executeCompensation, item.id)} onClick={() => executeCompensation.mutate(item.id)}><Play size={14} /></IconBtn>
                      )}
                      {item.status === "failed" && (
                        <IconBtn label="Retry compensation" busy={busy(retryCompensation, item.id)} onClick={() => retryCompensation.mutate(item.id)}><RotateCcw size={14} /></IconBtn>
                      )}
                      {["reconciling", "reconciled", "rejected"].includes(item.status) && <span className="text-xs text-muted">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {[approveCompensation, rejectCompensation, executeCompensation, retryCompensation].map((mutation, index) =>
        mutation.isError ? <div key={index} className="mt-2"><ErrorBox error={mutation.error} /></div> : null)}
    </>
  );
}

function IconBtn({ children, label, tone, busy, onClick }: {
  children: React.ReactNode; label: string; tone?: "good" | "bad"; busy?: boolean; onClick: () => void;
}) {
  const cls = tone === "good" ? "text-good" : tone === "bad" ? "text-bad" : "text-muted";
  return (
    <button title={label} aria-label={label} disabled={busy} onClick={onClick}
            className={`rounded-md border border-border p-1.5 hover:bg-border/40 ${cls}`}>
      {busy ? <Loader2 size={14} className="animate-spin" /> : children}
    </button>
  );
}

function CreateAction({ ws, pipelines, runs, integrations, onDone }: {
  ws: string; pipelines: Pipeline[]; runs: PipelineRun[]; integrations: Integration[]; onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [connector, setConnector] = useState("action.log");
  const [sourceRunId, setSourceRunId] = useState("");
  const [integrationId, setIntegrationId] = useState("");
  const selectedRun = runs.find((row) => row.id === sourceRunId);
  const selectedIntegration = integrations.find((row) => row.id === integrationId);
  const actionIntegrations = integrations.filter((row) => row.active && row.kind === "http_api" &&
    row.purpose === "governed_action" && !row.secret_ref);

  const create = useMutation({
    mutationFn: () => rawFetch(`/actions/definitions/`, {
      method: "POST",
      body: JSON.stringify({
        workspace: ws, name, connector_key: connector,
        requires_approval: true,
        config: {
          pipeline_revision_id: selectedRun?.pipeline_revision,
          ontology_version_id: selectedRun?.ontology_version,
          ...(connector === "action.webhook" ? {
            integration_id: selectedIntegration?.id,
            integration_revision: selectedIntegration?.revision,
            integration_digest: selectedIntegration?.digest,
          } : { channel: "decisions" }),
        },
      }),
    }),
    onSuccess: onDone,
  });

  return (
    <div className="card mb-4">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <label className="text-xs">Action name<input className="input mt-1" placeholder="e.g. Send reviewed decision" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <select className="input" value={connector} onChange={(e) => setConnector(e.target.value)}>
          <option value="action.log">Record an internal decision</option>
          <option value="action.webhook">Send to a reviewed HTTP integration</option>
        </select>
        <label className="text-xs md:col-span-2">Pin to a completed pipeline run
          <select className="input mt-1" value={sourceRunId} onChange={(e) => setSourceRunId(e.target.value)}>
            <option value="">Choose a completed run with a published ontology</option>
            {runs.filter((row) => row.pipeline_revision && row.ontology_version).map((row) =>
              <option key={row.id} value={row.id}>{pipelines.find((p) => p.id === row.pipeline)?.name ?? "Pipeline"} · {shortId(row.id)}</option>)}
          </select>
        </label>
        {connector === "action.webhook" && <label className="text-xs md:col-span-2">Reviewed integration
          <select className="input mt-1" value={integrationId} onChange={(e) => setIntegrationId(e.target.value)}>
            <option value="">Choose an active HTTP action integration</option>
            {actionIntegrations.map((row) => <option key={row.id} value={row.id}>{row.name} · revision {row.revision}</option>)}
          </select>
          {actionIntegrations.length === 0 && <span className="mt-1 block text-muted">Create an active, no-secret action integration in Developer portal first.</span>}
        </label>}
        <p className="text-xs text-muted md:col-span-2">Approval is required. The request contains run and version identifiers plus content hashes, never the pipeline input.</p>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button className="btn-primary" disabled={!name.trim() || !selectedRun || !selectedRun.pipeline_revision || !selectedRun.ontology_version ||
          (connector === "action.webhook" && !selectedIntegration) || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? <Loader2 className="mr-1 inline animate-spin" size={14} /> : null}
          Create
        </button>
        <button className="btn-ghost" onClick={onDone}>Cancel</button>
      </div>
      {create.isError && <div className="mt-2"><ErrorBox error={create.error} /></div>}
    </div>
  );
}
